interface IPoint {
  x: number
  y: number
}

// Parse an SVG path data string into points
// Returns an empty array when SVG geometry APIs are unavailable or throw
function parseSVGPath(svgPathData: string, numSamples: number = 100): IPoint[] {
  // This is a browser only function
  if (typeof window === 'undefined') return []
  const svgNamespace = 'http://www.w3.org/2000/svg'
  try {
    const pathElement = window.document.createElementNS(svgNamespace, 'path')
    if (
      typeof pathElement.getTotalLength !== 'function' ||
      typeof pathElement.getPointAtLength !== 'function'
    ) {
      return []
    }
    pathElement.setAttribute('d', svgPathData)
    // Sample points along the path
    const totalLength = pathElement.getTotalLength()
    const points: IPoint[] = []
    for (let i = 0; i <= numSamples; ++i) {
      const fraction = i / numSamples
      const point = pathElement.getPointAtLength(fraction * totalLength)
      points.push({ x: point.x, y: point.y })
    }
    return points
  } catch {
    return []
  }
}

// Interpolate points to get eased value for a given x
function interpolatePath(points: IPoint[], x: number): number {
  if (x <= 0) return points[0]?.y ?? 0
  if (x >= 1) return points.at(-1)?.y ?? 1

  for (let i = 0; i < points.length - 1; i++) {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const start = points[i] as IPoint
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const end = points[i + 1] as IPoint
    if (x >= start.x && x <= end.x) {
      const t = (x - start.x) / (end.x - start.x)
      return start.y * (1 - t) + end.y * t
    }
  }

  return 0
}

export interface TTweenEasing {
  (position: number): number
}

function easeInOut(x: number): number {
  return x < 0.5 ? 2 * x * x : -1 + (4 - 2 * x) * x
}

function _createEasingFromSVGPath(svgPathData: string): TTweenEasing {
  const parsedPath = parseSVGPath(svgPathData)
  if (parsedPath.length === 0) return easeInOut
  return (x: number) => interpolatePath(parsedPath, x)
}

export const tweenEasings = {
  // oxlint-disable-next-line typescript/consistent-type-assertions
  linear: (x => x) as TTweenEasing,
  // oxlint-disable-next-line typescript/consistent-type-assertions
  easeIn: (x => x * x) as TTweenEasing,
  // oxlint-disable-next-line typescript/consistent-type-assertions
  easeOut: (x => 1 - Math.pow(1 - x, 2)) as TTweenEasing,
  easeInOut,
  veryAggressiveOut: _createEasingFromSVGPath(
    'M0,0 C0.073,0.386 0.1,0.676 0.279,0.841 0.432,0.982 0.756,1 1,1'
  ),
  aggressiveInOut: _createEasingFromSVGPath(
    'M0,0 C0.238,0 0.204,0.407 0.286,0.608 0.418,0.934 0.59,1 1,1'
  )
} as const
